import { createAsyncThunk, createSlice, current } from "@reduxjs/toolkit";
import axios from "axios";
import fileDownload from "js-file-download";
import { api } from "../services/api-service";

import { closeEditSurveyModal, closeSurveyModal } from "./modalSlice";
import { success } from "./notificationSlice";

const initialState = {
  isUpdated: false,
  pageNumber: 1,
  pageSize: 20,
  surveys: {},
  survey: {
    status: "draft",
    inspection: [],
  },
  reportsRequested: [],
  viewClientIsActive: false,
};

export const getAllSurveys = createAsyncThunk(
  "getSurveys",
  async (params, { rejectWithValue, dispatch, getState }) => {
    try {
      const response = await api.get(
        `get-surveys?pageSize=${
          params.pageSize ? params.pageSize : getState().survey.pageSize
        }&pageNumber=${params.pageNum}&searchText=${params.searchText}${
          params.clientRef ? `&clientRef=${params.clientRef}` : ""
        }${params.status ? `&status=${params.status}` : ""}${
          params.startDate ? `&startDate=${params.startDate}` : ""
        }${params.endDate ? `&endDate=${params.endDate}` : ""}`
      );
      if (response?.ok) {
        dispatch(updatePage(params.pageNum));
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const getMySurveys = createAsyncThunk(
  "getMySurveys",
  async ({ user, search }, { rejectWithValue }) => {
    try {
      const response = await api.get(
        `my-survey/${user?.id}?searchText=${search}&pageSize=1000`
      );
      if (response?.ok) {
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const assignSurveys = createAsyncThunk(
  "assignSurveys",
  async (
    { surveyor, surveys, clientRef, pageNum },
    { rejectWithValue, dispatch }
  ) => {
    try {
      const response = await api.put(`assign-survey/${surveyor.id}`, surveys);
      if (response?.ok) {
        dispatch(success({ message: "Survey assigned successfully" }));
        dispatch(getAllSurveys({ pageNum, searchText: "", clientRef }));
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteSurveys = createAsyncThunk(
  "deleteSurveys",
  async ({ surveys, clientRef, pageNum }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`delete-surveys`, surveys);
      if (response?.ok) {
        dispatch(success({ message: "Survey(s) deleted successfully" }));
        dispatch(getAllSurveys({ pageNum, searchText: "", clientRef }));
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const submitSurvey = createAsyncThunk(
  "submitSurvey",
  async ({ survey, user }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`submit-survey/${survey.id}`, survey);
      if (response?.ok) {
        dispatch(success({ message: "Survey submitted successfully" }));
        dispatch(getMySurveys({ user, search: "" }));
        dispatch(closeSurveyModal());
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const publishSurvey = createAsyncThunk(
  "publishSurvey",
  async (survey, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`publish-survey/${survey.id}`);
      if (response?.ok) {
        dispatch(success({ message: "Survey published successfully" }));
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const deleteSurvey = createAsyncThunk(
  "deleteSurvey",
  async (survey, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.delete(`delete-survey/${survey.id}`);
      if (response?.ok) {
        dispatch(success({ message: "Survey deleted successfully" }));
        dispatch(getAllSurveys({ pageNum: 1, searchText: "" }));
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const surveyAdd = createAsyncThunk(
  "addSurvey",
  async (survey, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`add-survey`, {
        ...survey,
        wheRef: survey.clientRef + "/" + survey.wheRef,
      });
      if (response?.ok) {
        dispatch(success({ message: "Survey added successfully" }));
        dispatch(
          getAllSurveys({ pageNum: 1, searchText: "", clientRef: null })
        );
        dispatch(closeSurveyModal());
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const bulkUpload = createAsyncThunk(
  "bulkUpload",
  async ({ surveys }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`bulk-upload`, surveys);
      if (response?.ok) {
        dispatch(
          success({ message: "Survey bulk upload completed successfully" })
        );
        dispatch(
          getAllSurveys({ pageNum: 1, searchText: "", clientRef: null })
        );
        dispatch(closeSurveyModal());
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateSurvey = createAsyncThunk(
  "updateSurvey",
  async (survey, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`update-survey/${survey.id}`, {
        ...survey,
        wheRef: survey.clientRef + "/" + survey.wheRef,
      });
      if (response?.ok) {
        dispatch(
          addSurvey({
            ...survey,
            wheRef: survey.clientRef + "/" + survey.wheRef,
          })
        );
        dispatch(success({ message: "Survey updated successfully" }));
        dispatch(closeEditSurveyModal());
        dispatch(getAllSurveys({ pageNum: 1, searchText: "" }));
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateStatus = createAsyncThunk(
  "updateSurveyStatus",
  async ({ id, status, pageNum, clientRef }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(`update-status/${id}?status=${status}`);
      if (response?.ok) {
        dispatch(success({ message: "Survey status updated successfully" }));
        dispatch(
          getAllSurveys({ pageNum: pageNum, searchText: "", clientRef })
        );
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const updateSurveyInspection = createAsyncThunk(
  "updateSurveyInspection",
  async ({ inspection, survey }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.put(
        `update-inspection/${survey.id}/${inspection.id}`,
        inspection
      );
      if (response?.ok) {
        dispatch(closeEditSurveyModal());
        return { ...response };
      } else throw new Error({ ...response });
    } catch (error) {
      return rejectWithValue(error);
    }
  }
);

export const surveyReducer = createSlice({
  name: "survey",
  initialState,
  reducers: {
    setSurvey: (state, action) => {
      let payload = action.payload;
      state.survey[payload.field] = payload.value;
    },
    addSurvey: (state, action) => {
      state.survey = { ...action.payload };
    },
    addInspection: (state, action) => {
      let inspections = current(state).survey.inspection || [];
      inspections = [...inspections, action.payload];
      state.survey.inspection = inspections;
    },
    updateInspection: (state, action) => {
      let inspections = [...current(state).survey.inspection];
      inspections[action.payload.index] = action.payload.inspection;
      state.survey.inspection = inspections;
    },
    resetSurvey: (state, action) => {
      state.survey = initialState.survey;
    },
    updateAllSurveys: (state, action) => {
      state.surveys.data = action.payload;
    },
    updatePage: (state, action) => {
      state.pageNumber = action.payload;
    },
    requestReports: (state, action) => {
      state.reportsRequested = action.payload;
    },
    onUpdate: (state) => {
      state.isUpdated = true;
    },
    onDismiss: (state) => {
      state.isUpdated = false;
    },
    resetState: (state) => {
      state.pageNumber = 1;
      state.survey = {};
      state.surveys = {};
    },
    setClientIsActive: (state, action) => {
      state.viewClientIsActive = action.payload;
    },
    updatePageSize: (state, action) => {
      state.pageSize = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(getAllSurveys.fulfilled, (state, action) => {
        const allSurveys = action.payload.data.data;
        state.surveys = {
          ...allSurveys,
          data: allSurveys.data.map((sur) => {
            return { ...sur, checked: false };
          }),
        };
      })
      .addCase(getMySurveys.fulfilled, (state, action) => {
        state.surveys = action.payload.data.data;
      })
      .addCase(submitSurvey.fulfilled, (state) => {
        state.survey = initialState.survey;
      })
      .addCase(assignSurveys.fulfilled, (state) => {
        state.survey = initialState.survey;
      })
      .addCase(bulkUpload.fulfilled, (state) => {
        state.survey = initialState.survey;
      })
      .addCase(surveyAdd.fulfilled, (state) => {
        state.survey = initialState.survey;
      })
      .addCase(publishSurvey.fulfilled, (state) => {
        state.survey.status = "published";
      })
      .addCase(updateSurvey.fulfilled, (state) => {
        state.survey.status = "published";
      });
  },
});

// Action creators are generated for each case reducer function
export const {
  setSurvey,
  addSurvey,
  addInspection,
  updateInspection,
  resetSurvey,
  updateAllSurveys,
  updatePage,
  requestReports,
  resetState,
  setClientIsActive,
  onUpdate,
  updatePageSize,
  onDismiss,
} = surveyReducer.actions;

export const surveySelector = (state) => state.survey.survey;
export const surveysSelector = (state) => state.survey.surveys;
export const pageSelector = (state) => state.survey.pageNumber;
export const pageSizeSelector = (state) => state.survey.pageSize;
export const reportsSelector = (state) => state.survey.reportsRequested;
export const viewClientIsActive = (state) => state.survey.viewClientIsActive;
export const isUpdatedSelector = (state) => state.survey.isUpdated;

export default surveyReducer.reducer;
